import React, { useState } from "react";
import { Clock, Plane, Loader2, Save, ChevronDown, Copy } from "lucide-react";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { DAY_KEYS, DAY_LABELS, GMT_OFFSETS, formatGmt, defaultOpeningHours, formatOpeningHours } from "../../utils/hours";
import { useLang } from "../../context/LanguageContext";

const timeCls = "flex-1 min-w-0 border border-border rounded-lg px-2 py-2 text-sm bg-transparent text-foreground outline-none focus:border-[#16a34a]";

export const OpeningHoursCard = () => {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const [oh, setOh] = useState(() => user?.openingHours || defaultOpeningHours());
  const [vac, setVac] = useState(() => user?.vacation || { active: false, until: null, message: "" });
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const setDay = (d, patch) => setOh((p) => ({ ...p, days: { ...p.days, [d]: { ...p.days[d], ...patch } } }));

  // Most businesses keep the same weekday hours — copy Monday down instead of 7 manual edits.
  const copyMonday = () => {
    const mon = oh.days.mon || { closed: false, open: "09:00", close: "18:00" };
    setOh((p) => ({ ...p, days: Object.fromEntries(DAY_KEYS.map((d) => [d, { ...mon }])) }));
    toast({ title: t("hrs.mondayCopiedToEvery"), description: t("hrs.tweakAnyDayThat") });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/my/business", { openingHours: oh, vacation: vac });
      await refresh();
      toast({ title: t("hrs.hoursSaved"), description: t("hrs.yourOpeningHoursNow") });
    } catch {
      toast({ title: t("hrs.couldNotSave"), description: t("hrs.pleaseTryAgain") });
    } finally { setSaving(false); }
  };

  const summary = formatOpeningHours(oh) || "Not set yet";

  return (
    <div className="bg-card border border-border rounded-2xl shadow-sm" data-testid="opening-hours-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="opening-hours-toggle"
        aria-expanded={open}
        className="w-full flex items-start gap-3 p-4 sm:p-5 text-left"
      >
        <Clock size={18} className="text-[#16a34a] shrink-0 mt-0.5" />
        <span className="min-w-0 flex-1">
          <span className="font-bold text-foreground block">{t("hrs.openingHours")}</span>
          <span className="text-xs text-muted-foreground block mt-0.5 truncate" data-testid="opening-hours-summary">
            {vac.active ? "Holiday mode on · " : ""}{summary}
          </span>
        </span>
        <ChevronDown size={18} className={`text-muted-foreground shrink-0 mt-0.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 sm:px-5 pb-5 space-y-4" data-testid="opening-hours-body">
          <p className="text-xs text-muted-foreground">{t("hrs.setOnceHereEvery")}</p>

          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[9rem]">
              <label className="text-sm font-medium text-foreground block mb-1">{t("hrs.timeZone")}</label>
              <select
                data-testid="opening-hours-tz"
                value={oh.tzOffset}
                onChange={(e) => setOh((p) => ({ ...p, tzOffset: parseFloat(e.target.value) }))}
                className="w-full border border-border rounded-xl px-3 py-2.5 text-sm bg-transparent outline-none focus:border-[#16a34a] text-foreground"
              >
                {GMT_OFFSETS.map((o) => <option key={o} value={o}>{formatGmt(o)}</option>)}
              </select>
            </div>
            <button type="button" onClick={copyMonday} data-testid="copy-monday-btn" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#16a34a] border border-[#16a34a]/40 hover:bg-[#e7f6ec] dark:hover:bg-[#16341f] px-3 py-2.5 rounded-xl transition-colors">
              <Copy size={13} /> {t("hrs.copyMondayToAll")}
            </button>
          </div>

          <div className="divide-y divide-border rounded-xl border border-border">
            {DAY_KEYS.map((d) => {
              const day = oh.days[d] || { closed: false, open: "09:00", close: "18:00" };
              return (
                <div key={d} className="p-3 sm:flex sm:items-center sm:gap-3" data-testid={`hours-row-${d}`}>
                  <div className="flex items-center justify-between gap-3 sm:w-52 sm:shrink-0">
                    <span className="text-sm font-semibold text-foreground">{DAY_LABELS[d]}</span>
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer shrink-0">
                      <input type="checkbox" checked={!!day.closed} onChange={(e) => setDay(d, { closed: e.target.checked })} data-testid={`hours-closed-${d}`} className="accent-[#16a34a] h-4 w-4" /> Closed
                    </label>
                  </div>
                  {!day.closed && (
                    <div className="flex items-center gap-2 mt-2 sm:mt-0 sm:flex-1">
                      <input type="time" value={day.open} onChange={(e) => setDay(d, { open: e.target.value })} data-testid={`hours-open-${d}`} className={timeCls} />
                      <span className="text-muted-foreground text-xs shrink-0">to</span>
                      <input type="time" value={day.close} onChange={(e) => setDay(d, { close: e.target.value })} data-testid={`hours-close-${d}`} className={timeCls} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="rounded-xl border border-border p-4 bg-secondary/40 space-y-3">
            <label className="flex items-start gap-3 text-sm text-foreground cursor-pointer" data-testid="vacation-toggle">
              <input type="checkbox" checked={!!vac.active} onChange={(e) => setVac((p) => ({ ...p, active: e.target.checked }))} className="accent-[#16a34a] h-4 w-4 mt-0.5 shrink-0" />
              <span className="min-w-0">
                <span className="font-semibold flex items-center gap-1.5"><Plane size={15} className="text-[#16a34a]" /> {t("hrs.holidayVacationMode")}</span>
                <span className="text-muted-foreground text-xs block mt-0.5">{t("hrs.showsYourBusinessAs")}</span>
              </span>
            </label>
            {vac.active && (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="vacation-fields">
                <div>
                  <label className="text-xs font-medium text-foreground block mb-1">{t("hrs.reopenDateOptional")}</label>
                  <input type="date" value={vac.until ? vac.until.slice(0, 10) : ""} onChange={(e) => setVac((p) => ({ ...p, until: e.target.value || null }))} data-testid="vacation-until" className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-transparent text-foreground outline-none focus:border-[#16a34a]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground block mb-1">{t("hrs.messageOptional")}</label>
                  <input value={vac.message || ""} onChange={(e) => setVac((p) => ({ ...p, message: e.target.value }))} placeholder={t("hrs.eGBackOn")} data-testid="vacation-message" className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-transparent text-foreground outline-none focus:border-[#16a34a]" />
                </div>
              </div>
            )}
          </div>

          <button onClick={save} disabled={saving} data-testid="save-hours-btn" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-xl transition-colors">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save opening hours
          </button>
        </div>
      )}
    </div>
  );
};

export default OpeningHoursCard;
